// Общее для главной, раздела «Соревнования» и страницы соревнования.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGameweeks, useJoinCompetition, type Competition } from "@/api/queries";
import { errorMessage } from "@/api/client";
import { TeamNameDialog } from "./TeamNameDialog";

export const isManager = (c: Competition) => c.my_role === "owner" || c.my_role === "admin";

/** Можно ли собрать команду в соревновании без кода: открытое или я владелец/соадмин */
export const canCreateTeam = (c: Competition) =>
  !c.my_entry_id &&
  c.status !== "finished" &&
  (isManager(c) || (c.visibility === "public" && !c.join_closed));

export const accessText = (c: Competition) => (c.visibility === "public" ? "Открытое" : "Закрытое");

export function roleText(c: Competition): string {
  if (c.visibility === "public") return "Picked";
  if (c.my_role === "owner") return "вы владелец";
  if (c.my_role === "admin") return "вы соадмин";
  return "по приглашению";
}

/** Номера первого и последнего тура соревнования */
export function useTourRange(c: Competition): string | null {
  const gws = useGameweeks(c.season_id);
  const first = gws.data?.find((g) => g.id === c.first_gameweek_id)?.number;
  const last = gws.data?.find((g) => g.id === c.last_gameweek_id)?.number;
  return first && last ? `туры ${first}–${last}` : null;
}

/** «Создать команду»: спрашиваем название команды, вступаем в соревнование и открываем
 *  редактор состава. Названия по умолчанию нет: пользователь вводит своё. Страница
 *  выводит окно через {createTeam.dialog}. */
export function useCreateTeam() {
  const nav = useNavigate();
  const join = useJoinCompetition();
  const [target, setTarget] = useState<{ id: string; code?: string; name?: string } | null>(null);
  const [teamName, setTeamName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = (competitionId: string, joinCode?: string, competitionName?: string) => {
    setError(null);
    setTeamName("");
    setTarget({ id: competitionId, code: joinCode, name: competitionName });
  };
  const close = () => {
    if (!join.isPending) setTarget(null);
  };
  const confirm = async () => {
    if (!target || teamName.trim().length < 2) return;
    setError(null);
    try {
      const entry = await join.mutateAsync({
        competitionId: target.id,
        join_code: target.code,
        team_name: teamName.trim(),
      });
      setTarget(null);
      nav(`/entries/${entry.id}/edit`);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const dialog = target ? (
    <TeamNameDialog
      competitionId={target.id}
      competitionName={target.name}
      value={teamName}
      onChange={setTeamName}
      error={error}
      pending={join.isPending}
      onCancel={close}
      onConfirm={confirm}
    />
  ) : null;
  return {
    create,
    dialog,
    pending: join.isPending,
    pendingId: join.variables?.competitionId,
  };
}
